/**
 * Pure language-resolution logic. No i18next or platform imports so this can
 * be unit-tested and reused by tooling.
 */

import {
  DEFAULT_LANGUAGE,
  isLanguageEnabled,
  isSupportedLanguage,
  type SupportedLanguage,
} from './languages';

/** What the user can save in settings: follow the device, or a fixed language. */
export type LanguagePreference = 'system' | SupportedLanguage;

/** Split a locale tag into lowercase base + region, e.g. `es_MX` → `["es","mx"]`. */
function parseTag(tag: string | null | undefined): {
  base: string | null;
  region: string | null;
} {
  if (!tag) return { base: null, region: null };
  const parts = tag.toLowerCase().split(/[-_]/);
  return { base: parts[0] || null, region: parts[1] || null };
}

/**
 * Map a device locale tag to the best-fit enabled variant. Because we ship
 * regional variants, a bare base language resolves to a sensible regional
 * default (Latin-American Spanish, Brazilian Portuguese, France French).
 */
function pickVariant(
  tag: string | null | undefined,
): SupportedLanguage | null {
  const { base, region } = parseTag(tag);
  let candidate: SupportedLanguage | null = null;
  switch (base) {
    case 'en':
      candidate = 'en';
      break;
    case 'es':
      candidate = region === 'es' ? 'es-ES' : 'es-419';
      break;
    case 'pt':
      candidate = region === 'pt' ? 'pt-PT' : 'pt-BR';
      break;
    case 'fr':
      candidate = region === 'ca' ? 'fr-CA' : 'fr-FR';
      break;
    default:
      candidate = null;
  }
  return candidate && isLanguageEnabled(candidate) ? candidate : null;
}

/**
 * Resolve the language to use from a device locale tag (e.g. `es-MX`).
 * Unsupported or publicly disabled languages fall back to English.
 */
export function resolveAppLanguage(
  deviceLanguage?: string | null,
): SupportedLanguage {
  return pickVariant(deviceLanguage) ?? DEFAULT_LANGUAGE;
}

/**
 * Resolve the language to use from a saved preference plus the device locale.
 *
 * - No saved value, or `'system'` → resolve from the device locale.
 * - Saved language that is supported and enabled → use it.
 * - Saved language that is unknown, unsupported, or has since been disabled →
 *   fall back to the device language if that is enabled, otherwise English.
 */
export function resolveSavedLanguage(
  savedLanguage?: string | null,
  deviceLanguage?: string | null,
): SupportedLanguage {
  if (!savedLanguage || savedLanguage === 'system') {
    return resolveAppLanguage(deviceLanguage);
  }
  // An exact, still-enabled variant (e.g. 'es-ES') is used as-is.
  if (isSupportedLanguage(savedLanguage) && isLanguageEnabled(savedLanguage)) {
    return savedLanguage;
  }
  // Legacy base saves (e.g. 'es' from before regional variants) map to the
  // best variant; otherwise fall back to the device locale.
  return pickVariant(savedLanguage) ?? resolveAppLanguage(deviceLanguage);
}
